import { COMMENDA_OS_DATA_SOURCE } from '../../constants';
import type { ComponentCategory, ComponentTemplate } from '@commenda/types';

// Component templates with default configurations
export const COMPONENT_TEMPLATES: ComponentTemplate[] = [
  // Input Components
  {
    type: 'Input',
    name: 'Text Input',
    description: 'Single line text input',
    icon: 'Type',
    defaultConfig: {
      component: 'Input',
      fieldType: 'text',
      label: 'Text Input',
      placeholder: 'Enter text...',
    },
  },
  {
    type: 'Input',
    name: 'Email Input',
    description: 'Email address input with validation',
    icon: 'Mail',
    defaultConfig: {
      component: 'Input',
      fieldType: 'email',
      label: 'Email Address',
      placeholder: 'name@example.com',
      validation: {
        rules: {
          email: { enabled: true, message: 'Please enter a valid email' },
        },
      },
    },
  },
  {
    type: 'TextArea',
    name: 'Text Area',
    description: 'Multi-line text input',
    icon: 'AlignLeft',
    defaultConfig: {
      component: 'TextArea',
      label: 'Description',
      placeholder: 'Enter description...',
      props: {
        rows: 4,
        maxLength: 1000,
        showCharCount: true,
      },
    },
  },
  {
    type: 'NumberInput',
    name: 'Number Input',
    description: 'Numeric value input',
    icon: 'Hash',
    defaultConfig: {
      component: 'NumberInput',
      label: 'Number',
      props: {
        min: 0,
        max: 100,
        step: 1,
      },
    },
  },
  {
    type: 'PasswordInput',
    name: 'Password Input',
    description: 'Secure password input',
    icon: 'Lock',
    defaultConfig: {
      component: 'PasswordInput',
      label: 'Password',
      placeholder: 'Enter password...',
    },
  },
  {
    type: 'PhoneInput',
    name: 'Phone Input',
    description: 'International phone number input',
    icon: 'Phone',
    defaultConfig: {
      component: 'PhoneInput',
      label: 'Phone Number',
      props: {
        defaultCountry: 'US',
      },
    },
  },
  {
    type: 'UrlInput',
    name: 'URL/Domain Input',
    description: 'HTTPS URL or domain input with validation',
    icon: 'Link',
    defaultConfig: {
      component: 'UrlInput',
      label: 'Website URL',
      placeholder: 'https://example.com or example.com',
      validation: {
        rules: {
          url: {
            enabled: true,
            message: 'Please enter a valid HTTPS URL or domain',
          },
        },
      },
    },
  },
  {
    type: 'DateInput',
    name: 'Date Input',
    description: 'Date picker with calendar and manual input',
    icon: 'Calendar',
    defaultConfig: {
      component: 'DateInput',
      label: 'Date',
      placeholder: 'Select date',
    },
  },
  // Selection Components
  {
    type: 'Select',
    name: 'Dropdown Select',
    description: 'Single selection dropdown',
    icon: 'ChevronDown',
    defaultConfig: {
      component: 'Select',
      label: 'Select Option',
      placeholder: 'Choose an option...',
      options: [
        { value: 'option1', label: 'Option 1' },
        { value: 'option2', label: 'Option 2' },
      ],
    },
  },
  {
    type: 'RadioGroup',
    name: 'Radio Group',
    description: 'Single selection radio buttons',
    icon: 'Circle',
    defaultConfig: {
      component: 'RadioGroup',
      label: 'Select One',
      flexDirection: 'column',
      options: [
        { value: 'option1', label: 'Option 1' },
        { value: 'option2', label: 'Option 2' },
      ],
    },
  },
  {
    type: 'CheckboxGroup',
    name: 'Checkbox Group',
    description: 'Multiple selection checkboxes',
    icon: 'CheckSquare',
    defaultConfig: {
      component: 'CheckboxGroup',
      options: [
        { value: 'option1', label: 'Option 1' },
        { value: 'option2', label: 'Option 2' },
      ],
    },
  },
  // {
  //   type: 'AsyncSelect',
  //   name: 'Async Select',
  //   description: 'Dropdown with API-loaded options',
  //   icon: 'Search',
  //   defaultConfig: {
  //     component: 'AsyncSelect',
  //     label: 'Search and Select',
  //     placeholder: 'Type to search...',
  //     dataSource: {
  //       type: 'api',
  //       endpoint: '',
  //       searchable: true,
  //       minSearchLength: 3,
  //     },
  //   },
  // },
  // File & Address
  {
    type: 'FileUpload',
    name: 'File Upload',
    description: 'Single file uploader',
    icon: 'Upload',
    defaultConfig: {
      component: 'FileUpload',
      label: 'Upload File',
      props: {
        maxFiles: 1,
        acceptedFileTypes: ['.pdf', '.doc', '.docx', '.jpg', '.png'],
        maxSizeInMB: 10,
      },
    },
  },
  {
    type: 'MultiFileUpload',
    name: 'Multi File Upload',
    description: 'Multiple file uploader',
    icon: 'Upload',
    defaultConfig: {
      component: 'MultiFileUpload',
      label: 'Upload Files',
      props: {
        acceptedFileTypes: ['.pdf', '.doc', '.docx', '.jpg', '.png'],
        maxSizeInMB: 10,
        maxFiles: 5,
      },
    },
  },
  {
    type: 'AddressInput',
    name: 'Address Input',
    description: 'Full address input with autocomplete',
    icon: 'MapPin',
    defaultConfig: {
      component: 'AddressInput',
      label: 'Address',
      props: {
        enableAutocomplete: true,
        defaultCountry: 'US',
      },
    },
  },
  {
    type: 'CountryInput',
    name: 'Country Select',
    description: 'Country selection with flags and search',
    icon: 'Globe',
    defaultConfig: {
      component: 'CountryInput',
      label: 'Country',
      placeholder: 'Select country',
      props: {
        type: 'single',
      },
    },
  },
  // Display Components
  {
    type: 'Alert',
    name: 'Alert',
    description: 'Information or warning message',
    icon: 'AlertCircle',
    defaultConfig: {
      component: 'Alert',
      props: {
        message: 'This is an informational alert.',
        type: 'info',
        dismissible: true,
      },
    },
  },
  // {
  //   type: 'Button',
  //   name: 'Button',
  //   description: 'Action button',
  //   icon: 'MousePointer',
  //   defaultConfig: {
  //     component: 'Button',
  //     props: {
  //       label: 'Click Me',
  //       variant: 'primary',
  //       size: 'md',
  //     },
  //   },
  // },
  // Advanced Components
  // {
  //   type: 'DynamicFieldArray',
  //   name: 'Repeatable Fields',
  //   description: 'Dynamic list of fields that can be added/removed',
  //   icon: 'List',
  //   defaultConfig: {
  //     component: 'DynamicFieldArray',
  //     label: 'Items',
  //     props: {
  //       minItems: 1,
  //       maxItems: 10,
  //       addButtonText: 'Add Item',
  //     },
  //     template: [
  //       {
  //         id: 'item-name',
  //         component: 'Input',
  //         fieldName: 'name',
  //         label: 'Name',
  //         order: 1,
  //       },
  //     ],
  //   },
  // },
  // {
  //   type: 'NameAvailabilityChecker',
  //   name: 'Name Checker',
  //   description: 'Input with availability checking',
  //   icon: 'CheckCircle',
  //   defaultConfig: {
  //     component: 'NameAvailabilityChecker',
  //     label: 'Entity Name',
  //     placeholder: 'Enter name to check...',
  //     props: {
  //       suffix: 'LLC',
  //       showSuggestions: true,
  //     },
  //   },
  // },
  // {
  //   type: 'UpsellCard',
  //   name: 'Upsell Card',
  //   description: 'Promotional card with features and pricing',
  //   icon: 'CreditCard',
  //   defaultConfig: {
  //     component: 'UpsellCard',
  //     props: {
  //       title: 'Premium Feature',
  //       description: 'Unlock additional capabilities',
  //       features: ['Feature 1', 'Feature 2', 'Feature 3'],
  //       pricing: {
  //         display: 'Starting at $XX/month',
  //       },
  //     },
  //   },
  // },
  {
    type: 'FormSummary',
    name: 'Form Summary',
    description: 'Display summary of form data',
    icon: 'FileText',
    defaultConfig: {
      component: 'FormSummary',
      props: {
        sections: [],
        editable: true,
        showMissingFields: true,
        groupByStep: true,
      },
    },
  },
  {
    type: 'NestedForm',
    name: 'Nested Form',
    description: 'Open a linked form in a bottom-sheet drawer',
    icon: 'LinkExternal01',
    defaultConfig: {
      component: 'NestedForm',
      label: 'Section title',
      description: 'Section description',
      props: {
        keyId: '',
        buttonText: 'Add',
      },
    },
  },
];

// Commenda OS – Business Entity scoped components
export const COS_BUSINESS_ENTITY_TEMPLATES: ComponentTemplate[] = [
  {
    type: 'EntitySelect',
    name: 'Entity Select',
    description: 'Select a business entity from Commenda OS',
    icon: 'Building2',
    defaultConfig: {
      component: 'EntitySelect',
      label: 'Select Entity',
      props: {
        entity: 'corporation',
        mode: 'select',
        displayField: 'generalCorporationInfo.legalName',
        valueField: 'id',
        listPath: 'company.corporations',
      },
    },
  },
  {
    type: 'AutoDetectField',
    name: 'Auto Detect Field',
    description: 'Auto-maps to input type based on Commenda OS schema',
    icon: 'Sparkles',
    defaultConfig: {
      component: 'AutoDetectField',
      label: 'Auto Field',
      props: {},
      dataSource: {
        type: COMMENDA_OS_DATA_SOURCE,
        commendaOS: {
          entity: 'corporation',
          field: {
            read: '',
          },
          mode: 'read-write',
          contextBindings: [],
        },
      },
    },
  },
  {
    type: 'GovernmentIdentifier',
    name: 'Business ID',
    description: 'Collect business identifiers (EIN, state tax numbers, etc.)',
    icon: 'IdCard',
    defaultConfig: {
      component: 'GovernmentIdentifier',
      label: 'Business ID',
      props: {
        identifierTypeId: null,
        mode: 'read-write',
      },
      dataSource: {
        type: COMMENDA_OS_DATA_SOURCE,
        commendaOS: {
          entity: 'governmentIdentifier',
          contextBindings: [],
        },
      },
    },
  },
  {
    type: 'BankAccount',
    name: 'Bank Account',
    description:
      'Collect bank account details (account number, routing number, etc.)',
    icon: 'CreditCard01',
    defaultConfig: {
      component: 'BankAccount',
      label: 'Bank Account',
      props: {
        mode: 'read-write',
        defaultCountry: 'US',
        addNewLabel: 'Add new bank account',
      },
      dataSource: {
        type: COMMENDA_OS_DATA_SOURCE,
        commendaOS: {
          entity: 'bankAccount',
          contextBindings: [],
        },
      },
    },
  },
  {
    type: 'Ownership',
    name: 'Ownership',
    description:
      "Capture the company's cap table — owners/members/partners, shares or units, and contributions.",
    icon: 'PieChart01',
    defaultConfig: {
      component: 'Ownership',
      label: 'Company Ownership',
      // Seeds the dedicated section's title (label) + description shown by the
      // module card — the node renders only its content, no inner accordion.
      description:
        "Capture the structure of your entity's ownership.",
      props: {
        variant: 'shares',
      },
      dataSource: {
        type: COMMENDA_OS_DATA_SOURCE,
        commendaOS: {
          entity: 'corporationOwnership',
          contextBindings: [],
        },
      },
    },
  },
  {
    type: 'IndirectTaxRegistration',
    name: 'Indirect Tax Registration',
    description:
      "Registration inputs for the workflow's jurisdiction — tax type, frequency, and effective date. Writes the registration to the sales-tax API on submit.",
    icon: 'Globe02',
    defaultConfig: {
      component: 'IndirectTaxRegistration',
      label: 'Indirect Tax Registration',
      props: {},
    },
  },
  {
    type: 'Location',
    name: 'Location',
    description:
      'Collect business addresses (registered office, operating address, etc.)',
    icon: 'MapPin',
    defaultConfig: {
      component: 'Location',
      label: 'Location',
      props: {
        locationType: null,
        mode: 'read-write',
        defaultCountry: 'US',
      },
      dataSource: {
        type: COMMENDA_OS_DATA_SOURCE,
        commendaOS: {
          entity: 'location',
          contextBindings: [],
        },
      },
    },
  },
  {
    type: 'RegisteredAgent',
    name: 'Registered Agent',
    description:
      'Collect registered agent name and address for the corporation',
    icon: 'UserSquare',
    defaultConfig: {
      component: 'RegisteredAgent',
      label: 'Registered Agent',
      props: {
        mode: 'read-write',
        defaultCountry: 'US',
      },
      dataSource: {
        type: COMMENDA_OS_DATA_SOURCE,
        commendaOS: {
          entity: 'corporation',
          field: {
            read: 'generalCorporationInfo.registeredAgent',
            update: 'registeredAgent',
          },
          mode: 'read-write',
          contextBindings: [],
        },
      },
    },
  },
  {
    type: 'Document',
    name: 'Document',
    description:
      'Upload or view entity documents (COI, Articles of Association, Bylaws, EIN, etc.)',
    icon: 'FileText',
    defaultConfig: {
      component: 'Document',
      label: 'Document',
      props: {
        documentSubType: null,
        mode: 'read-write',
      },
      dataSource: {
        type: COMMENDA_OS_DATA_SOURCE,
        commendaOS: {
          entity: 'document',
          contextBindings: [],
        },
      },
    },
  },
  {
    type: 'BusinessEntitySelectOrCreate',
    name: 'Business Entity Select or Create',
    description:
      'Select existing or create new business entities with cascading country/jurisdiction/type',
    icon: 'Building2',
    defaultConfig: {
      component: 'BusinessEntitySelectOrCreate',
      label: 'Add Business Entity(s)',
      props: {
        displayField: 'generalCorporationInfo.legalName',
        valueField: 'id',
        listPath: 'company.corporations',
        minSelection: 1,
        maxSelection: 1,
        addNewLabel: 'Add new business entity',
        selectLabel: 'Add or select business entities',
      },
      template: [], // Extra fields beyond required (legalName, country, jurisdiction, entityType)
    },
  },
  {
    type: 'DynamicFields',
    name: 'Dynamic Fields',
    description:
      'Renders form fields dynamically from Content Library, another field, context, or static config',
    icon: 'Layers',
    defaultConfig: {
      component: 'DynamicFields',
      label: 'Dynamic Fields',
      props: {
        fieldsSource: {
          type: 'content-library',
          contentId: '',
          inputs: [],
        },
        fieldPrefix: '',
        layout: 'vertical',
      },
    },
  },
];

// Commenda OS – Key Persons scoped components
export const COS_KEY_PERSONS_TEMPLATES: ComponentTemplate[] = [
  {
    type: 'DirectorDocument',
    name: 'Director Document',
    description:
      'Upload or view director documents (Government ID, PAN Card, Aadhar Card, etc.)',
    icon: 'FileText',
    defaultConfig: {
      component: 'DirectorDocument',
      label: 'Director Document',
      props: {
        documentKind: null,
        mode: 'read-write',
      },
      dataSource: {
        type: COMMENDA_OS_DATA_SOURCE,
        commendaOS: {
          entity: 'directorDocument',
          contextBindings: [],
        },
      },
    },
  },
  {
    type: 'KeyPersons',
    name: 'Key Persons',
    description:
      'Select or add key persons (directors) for the company, with a linked form per person',
    icon: 'Users01',
    defaultConfig: {
      component: 'KeyPersons',
      label: 'Key Persons',
      props: {
        mode: 'select-add',
        keyId: '',
        minSelection: 1,
        maxSelection: 0,
        addNewLabel: 'Add new key person',
      },
    },
  },
  // KeyPersonLocation removed from palette: use AutoDetectField with entity KeyPerson and field
  // "Mailing Address" or "Operating Address" for single-shot create/update in Key Persons linked form.
];

// Legacy export: all Commenda OS templates (for any code that still references it)
export const COMMENDA_OS_TEMPLATES: ComponentTemplate[] = [
  ...COS_BUSINESS_ENTITY_TEMPLATES,
  ...COS_KEY_PERSONS_TEMPLATES,
];

// Canonical set of Commenda OS component `type` slugs, derived from the templates above.
// Single source of truth — any new entry added to the COS template arrays automatically
// becomes a known Commenda OS component everywhere this set is consumed (form canvas
// badge, palette filters, downstream rendering helpers, etc.).
export const COMMENDA_OS_COMPONENT_TYPES: ReadonlySet<string> = new Set(COMMENDA_OS_TEMPLATES.map(t => t.type));

// Helper: identifies whether a component instance (from a form's DSL) is a Commenda OS
// component. Covers both the data-source-bound variants (dataSource.type === 'commenda-os')
// and the linked-form/utility variants (KeyPersons, DynamicFields) that
// have no dataSource binding.
export function isCommendaOSComponentInstance(component: { component: string; dataSource?: { type?: string } }): boolean {
  if (component.dataSource?.type === COMMENDA_OS_DATA_SOURCE) return true;
  return COMMENDA_OS_COMPONENT_TYPES.has(component.component);
}

// Organize templates by category
export const COMPONENT_CATEGORIES: ComponentCategory[] = [
  // Commenda OS – Business Entity
  {
    id: 'commenda-os-business-entity',
    name: 'Business Entity',
    components: COS_BUSINESS_ENTITY_TEMPLATES,
  },
  // Commenda OS – Key Persons
  {
    id: 'commenda-os-key-persons',
    name: 'Key Persons',
    components: COS_KEY_PERSONS_TEMPLATES,
  },
  {
    id: 'inputs',
    name: 'Text Inputs',
    components: COMPONENT_TEMPLATES.filter((t) =>
      [
        'Input',
        'TextArea',
        'NumberInput',
        'PasswordInput',
        'PhoneInput',
        'UrlInput',
        'DateInput',
      ].includes(t.type)
    ),
  },
  {
    id: 'selection',
    name: 'Selection',
    components: COMPONENT_TEMPLATES.filter((t) =>
      ['Select', 'RadioGroup', 'CheckboxGroup'].includes(t.type)
    ),
  },
  {
    id: 'files-address',
    name: 'Files & Address',
    components: COMPONENT_TEMPLATES.filter((t) =>
      [
        'FileUpload',
        'MultiFileUpload',
        'AddressInput',
        'CountryInput',
      ].includes(t.type)
    ),
  },
  {
    id: 'display',
    name: 'Display',
    components: COMPONENT_TEMPLATES.filter((t) =>
      ['Alert', 'FormSummary', 'NestedForm'].includes(t.type)
    ),
  },
];
